import React, { useContext, useEffect, useMemo, useState } from 'react';
import { adobeSetProduct } from '@adsk/ha-adobe-dotcom-logger-util';
import lightTheme from '@digital-hig/theme-data/lib/build/json/lightTheme/nestedRoles.json';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { useSelector } from 'react-redux';
import { t } from '@lingui/macro';
import styled from 'styled-components';
import ThemeMui from '@digital-hig/theme-mui/lib/theme-mui';
import { adpDotcomLoggerUtil } from '@adsk/ha-adp-dotcom-logger-util';
import {
  activateLanguage,
  defaultLanguageCountry
} from '../utils/languageSupport';
import { productPageLink } from '../utils/productUtil';
import { ENVIRONMENT } from '../common/constants';
import EnvContext from '../common/envContext';
import RecommendedContent from '../components/RecommendedContent';
import BrowseByType from '../components/BrowseByType';
import InfoBox from '../components/InfoBox';
import SearchMFELoader from '../common/SearchMFELoader/SearchMFELoader';
import ErrorView from '../components/error';
import SearchHeading from '../common/SearchHeading/SearchHeading';
import { checkElement } from '../../App';

const Product = () => {
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const language = envContext.LANGUAGE || defaultLanguageCountry;
  const {
    bannerBoxData,
    customContent,
    customContentHtml,
    error,
    productListCode,
    recommendedContent,
    productSlug,
    generalTopics,
    contactSupport
  } = useSelector((state) => state.productData);

  const [productName, setProductName] = useState('');
  const adpConfig = envContext.ADP_CONFIG;
  const server = {
    adpConfig
  };
  const tracker = adpDotcomLoggerUtil(server);

  // e.g es-MX -> es-ES -> es, en-AU -> en, ko-KR -> kr
  const productPageUrl = useMemo(
    () => productPageLink(language, productSlug),
    [language, productSlug]
  );

  useEffect(() => {
    tracker.track('page_view', { ev: '.etx-root' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    sessionStorage.setItem('product-name', productSlug);
    if (bannerBoxData?.productName) {
      setProductName(bannerBoxData.productName);
      adobeSetProduct(bannerBoxData.productName);
    }
  }, [bannerBoxData?.productName, productSlug]);

  useEffect(() => {
    if (productListCode) {
      adobeSetProduct(productListCode);
    }
  }, [productListCode]);

  useEffect(() => {
    activateLanguage(language);
  }, [language]);

  useEffect(() => {
    // InfoBox
    checkElement('.dhig-theme--dark a', false).then((selector) => {
      selector.forEach((anchor) => {
        anchor.addEventListener('click', (e) => {
          e.preventDefault();
          const trackerEvent = e.target.href;
          tracker.track('header_link_click', { ev: trackerEvent });
          window.top.location.href = trackerEvent;
        });
      });
    });

    // BrowseTopics
    checkElement('.xf-general-topics a', false).then((selector) => {
      selector.forEach((anchor) => {
        anchor.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            const parentA = e.target.closest('a');
            tracker.track('emb_link_click', { ev: parentA.href });
            window.top.location.href = parentA.href;
          },
          true
        );
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeMui>
      <I18nProvider i18n={i18n}>
        {error.hasError !== undefined && error.hasError ? (
          <ErrorView />
        ) : (
          <>
            <div className="dhig-theme--dark">
              <div className="page-container-wrapper">
                <div className="page-container-inner dhig-pt-8 dhig-pb-6">
                  {bannerBoxData?.productBadge ? (
                    <a
                      href={productPageUrl}
                      onClick={(e) => {
                        e.preventDefault();
                        window.top.location = productPageUrl;
                      }}
                    >
                      <img
                        src={bannerBoxData?.productBadge}
                        alt={productName}
                        style={{ height: 50, width: 50, cursor: 'pointer' }}
                      />
                    </a>
                  ) : null}
                  <h1 className="headline-large dhig-typography-headline-large dhig-mb-6">
                    {t`${productName} Support`}
                  </h1>
                  <div className="MuiDivider-root MuiDivider-light" />
                  <InfoBox />
                </div>
              </div>
            </div>
            {!customContent ? (
              <div className="dhig-theme--light">
                <div className="page-container-wrapper">
                  <SearchMFEWrapper className="page-container-inner">
                    {window.cfp !== undefined ? (
                      <SearchHeading productName={productName} />
                    ) : null}
                    {productName !== undefined && productName !== '' ? (
                      <div className="dhig-pb-8" style={{ minHeight: 75 }}>
                        <SearchMFELoader
                          product={productName}
                          env={env}
                          language={language}
                          variant="productPage"
                        />
                      </div>
                    ) : null}
                    <div className="MuiDivider-root MuiDivider-light" />
                  </SearchMFEWrapper>
                  {generalTopics.length ? (
                    <BrowseTopicsWrapper id="etx-product">
                      <div
                        id="browse-general-topics"
                        className="etx-experience-fragment xf-general-topics"
                      >
                        {parse(generalTopics)}
                      </div>
                    </BrowseTopicsWrapper>
                  ) : null}
                </div>
                {contactSupport.length ? (
                  <ContactSupportWrapper>
                    <div id="etx-product">
                      <div className="MuiDivider-root MuiDivider-light" />
                      <div
                        id="contact-support"
                        className="etx-experience-fragment"
                      >
                        {parse(contactSupport)}
                      </div>
                    </div>
                  </ContactSupportWrapper>
                ) : null}

                <RecommendedContentWrapper
                  className={`page-container-wrapper ${
                    recommendedContent.length ? 'dhig-pt-8 dhig-pb-9' : ''
                  }`}
                >
                  <div className="page-container-inner recommended-content">
                    <RecommendedContent
                      language={language}
                      env={env}
                      product={productName}
                    />
                  </div>
                </RecommendedContentWrapper>

                <div className="page-container-wrapper">
                  <BrowseByTypeWrapper className="page-container-inner dhig-pb-4">
                    <div className="MuiDivider-root MuiDivider-light" />
                    <div className="browse-by-type dhig-pt-8">
                      <BrowseByType
                        env={env}
                        language={language}
                        product={productName}
                        context={`/support/technical/product/${productName}`}
                      />
                    </div>
                  </BrowseByTypeWrapper>
                </div>
              </div>
            ) : (
              <CustomContentWrapper className="page-container-wrapper">
                {customContentHtml !== undefined ? (
                  <div id="etx-product" className="dhig-pt-6 dhig-pb-8">
                    {parse(customContentHtml)}
                  </div>
                ) : null}
              </CustomContentWrapper>
            )}
          </>
        )}
      </I18nProvider>
    </ThemeMui>
  );
};

const SearchMFEWrapper = styled.div`
  .dhig-theme--light .MuiInputBase-input {
    line-height: inherit;
  }
`;

const ContactSupportWrapper = styled.div`
  .dhig-theme--light {
    background: ${lightTheme.tokens.colors['background-panel'].color};
  }
`;

// Link-list styles don't exist on DHIG side yet. Once added in DHIG and to the css, we
// should be able to remove the below styles, which were grabbed from the AEM implementation
// See: https://git.autodesk.com/WebDelivery/dotcom/blob/011da4d06f026f164fdb4a27f10c650e1592fc23/ui.frontend/src/main/webpack/dhig/components/link-list/link-list.scss
const BrowseTopicsWrapper = styled.div`
  .cmp-link-list {
    .text-link {
      a.cmp-text-link__link:focus {
        outline: 2px solid ${lightTheme.tokens.colors['fill-engage'].color};
      }

      a.cmp-text-link__link:hover {
        text-decoration: none;
      }

      a.cmp-text-link__link:active {
        outline: none;
      }
      a.MuiLink-underlineHover.MuiTypography-root {
        display: flex;
        padding: ${lightTheme.primitives.spacings['3']} 0;
        outline-offset: 0;
      }
      a.cmp-text-link__link.MuiLink-underlineHover.MuiTypography-root:hover {
        background: ${lightTheme.tokens.gradients[
          'row-highlighted-hover-loud-reverse'
        ].background};
      }
      a.cmp-text-link__link.MuiLink-underlineHover.MuiTypography-root:focus-visible,
      a.cmp-text-link__link.MuiLink-underlineHover.MuiTypography-root:focus {
        outline-offset: 0;
        padding-left: ${lightTheme.primitives.spacings['3']};
      }
      a.cmp-text-link__link.MuiLink-underlineHover.MuiTypography-root:active {
        background: ${lightTheme.tokens.gradients[
          'row-selected-pressed-loud-reverse'
        ].background};
        padding: ${lightTheme.primitives.spacings['3']} 0;
      }
    }
  }
`;

const RecommendedContentWrapper = styled.div`
  .MuiButton-root:hover {
    background-color: transparent;
  }
`;

const BrowseByTypeWrapper = styled.div`
  .dhig-flex p {
    margin-top: 0;
  }
`;
const CustomContentWrapper = styled.div`
  p {
    margin: 0 auto;
  }
`;

Product.propTypes = {
  /**
   * The product to search for
   */
  match: PropTypes.shape({
    params: PropTypes.shape({
      productName: PropTypes.string
    })
  })
};

Product.defaultProps = {
  /**
   * Default product
   */
  match: {
    params: {
      productName: ''
    }
  }
};

export default Product;
